import { createDeepgramAppendEngine } from "./createDeepgramAppendEngine";
import type { DeepgramStreamingEngine } from "./deepgramStreamingEngine";

export type DeepgramHoldMicStatus = "idle" | "starting" | "listening" | "stopping" | "error";

export type DeepgramHoldMicEngine = Pick<
  DeepgramStreamingEngine,
  "destroy" | "initModel" | "isInitialized" | "reset" | "start" | "stop"
>;

export type DeepgramHoldMicSessionDeps = {
  onAppend: (chunk: string) => void;
  maxDurationMsRef?: { current: number | undefined };
  onStatus?: (status: DeepgramHoldMicStatus) => void;
  onError?: (message: string | null) => void;
  onMaxDuration?: () => void;
  createEngine?: (onAppend: (chunk: string) => void) => DeepgramHoldMicEngine;
  scheduleTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (id: ReturnType<typeof setTimeout>) => void;
};

export class DeepgramHoldMicSession {
  private status: DeepgramHoldMicStatus = "idle";
  private engine: DeepgramHoldMicEngine | null = null;
  private maxDurationTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onAppendRef = { current: (_chunk: string) => undefined as void };
  private readonly deps: DeepgramHoldMicSessionDeps;

  constructor(deps: DeepgramHoldMicSessionDeps) {
    this.deps = deps;
    this.onAppendRef.current = deps.onAppend;
  }

  getStatus(): DeepgramHoldMicStatus {
    return this.status;
  }

  setOnAppend(onAppend: (chunk: string) => void): void {
    this.onAppendRef.current = onAppend;
  }

  async start(): Promise<void> {
    if (this.status === "listening" || this.status === "starting") return;
    this.setStatus("starting");
    this.setError(null);
    try {
      const engine = this.ensureEngine();
      engine.reset();
      if (!engine.isInitialized()) await engine.initModel();
      await engine.start();
      this.armMaxDurationTimer();
      this.setStatus("listening");
    } catch (e) {
      this.clearMaxDurationTimer();
      this.setStatus("error");
      this.setError(e instanceof Error ? e.message : "Failed to start mic.");
    }
  }

  async stop(): Promise<void> {
    if (this.status !== "listening") return;
    this.clearMaxDurationTimer();
    this.setStatus("stopping");
    try {
      await this.engine?.stop();
    } finally {
      this.setStatus("idle");
    }
  }

  destroy(): void {
    this.clearMaxDurationTimer();
    try {
      this.engine?.destroy();
    } catch {
      /* engine cleanup is best-effort */
    }
    this.engine = null;
    this.setStatus("idle");
    this.setError(null);
  }

  private ensureEngine(): DeepgramHoldMicEngine {
    if (this.engine) return this.engine;
    const createEngine =
      this.deps.createEngine ??
      ((onAppend: (chunk: string) => void) => {
        const appendRef = { current: onAppend };
        return createDeepgramAppendEngine(appendRef);
      });
    this.engine = createEngine((chunk) => this.onAppendRef.current(chunk));
    return this.engine;
  }

  private armMaxDurationTimer(): void {
    const maxDurationMs = this.deps.maxDurationMsRef?.current;
    if (!maxDurationMs || maxDurationMs <= 0) return;
    const scheduleTimer = this.deps.scheduleTimer ?? setTimeout;
    this.maxDurationTimer = scheduleTimer(() => {
      void this.handleMaxDuration();
    }, maxDurationMs);
  }

  private async handleMaxDuration(): Promise<void> {
    if (this.status !== "listening") return;
    this.clearMaxDurationTimer();
    this.setStatus("stopping");
    try {
      await this.engine?.stop();
    } finally {
      this.setStatus("idle");
      this.deps.onMaxDuration?.();
    }
  }

  private clearMaxDurationTimer(): void {
    if (this.maxDurationTimer == null) return;
    const clearTimer = this.deps.clearTimer ?? clearTimeout;
    clearTimer(this.maxDurationTimer);
    this.maxDurationTimer = null;
  }

  private setStatus(status: DeepgramHoldMicStatus): void {
    this.status = status;
    this.deps.onStatus?.(status);
  }

  private setError(message: string | null): void {
    this.deps.onError?.(message);
  }
}
